/* eslint-disable @typescript-eslint/no-unused-vars */
import { getApi, putApi } from 'redux/apis';
import Table from './Components/table';
import { hasText } from 'utils/utils';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useDispatch, useSelector } from 'react-redux';
import CustomConfigurationAddRuleModal from '../AddRule';
import {
    CHAT_MAPPING_RULE_EVENT,
    consumeChatMappingRule,
    peekChatMappingRule,
} from 'utils/pendingChatMappingRule';
import { classAction } from 'redux/action/classAction';
import { customerOptionsAction } from 'redux/action/customerAction';
import { getEmployeeAction } from 'redux/action/employeeAction';
import { AppDispatch } from 'redux/store';

export const CustomMappingRuleTable = (props: any) => {

    const { payPeriodId, isModalOpen, handleModalClose } = props;

    const [modalData, setModalData] = useState(null);
    const [isCopy, setIsCopy] = useState<boolean | null | undefined>(false);

    const [searchValue, setSearchValue] = useState<string>('');
    const [debounceSearchValue] = useDebounce(searchValue, 1000);
    const [isSearchable, setIsSearchable] = useState(false);
    const [status, setStatus] = useState<string | null>(null);
    const [tableData, setTableData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const { selectedCompanyDetails } = useSelector(
        (state: any) => state.companies
    );

    const dispatch = useDispatch<AppDispatch>();

    const { dataChanged } = useSelector((state: any) => state?.chatBubble);
    // Needed to know whether the Add Rule modal can safely be opened yet — see
    // openFromChatRequest.
    const { data: configurations } = useSelector((state: any) => state?.configuration);
    const { data: employeeList } = useSelector((state: any) => state.employees);
    const { data: classList } = useSelector((state: any) => state.class);
    const { optionData: customerList } = useSelector(
        (state: any) => state.customer
    );

    useEffect(() => {
        dispatch(getEmployeeAction());
        dispatch(classAction());
        dispatch(customerOptionsAction());
    }, []);

    const employeeOptions =
        employeeList?.map((employee: any) => ({
            value: employee?.id,
            title: employee?.fullName,
        })) || [];

    const classOptions =
        classList?.map((singleClass: any) => ({
            value: singleClass?.Id,
            title: singleClass?.FullyQualifiedName,
        })) || [];

    const getCustomRulesData = async () => {
        setIsLoading(true);
        const query: any = {
            search: searchValue,
            status,
            payPeriodId,
            companyId: localStorage.getItem(
                'companyId'
            )
        };

        if (!hasText(searchValue)) {
            delete query.search;
        }

        if (!hasText(status)) {
            delete query.status;
        }

        try {
            const res = await getApi(
                `/custom-configuration-rule`,
                query
            );

            if (res.data?.data?.content) {
                setTableData(res.data.data.content)
            }

        } catch (error) {
            //
            console.log(error);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        getCustomRulesData();
    }, [selectedCompanyDetails, payPeriodId]);

    // A rule created from the AI chat's builder card lands straight in the DB,
    // so without this the table keeps showing the pre-create list until the
    // drawer is reopened or the period switched. Same pattern the Cost
    // Allocation table uses: re-run this component's OWN fetch on a matching
    // notice rather than have the chat guess the current filters.
    useEffect(() => {
        if (!dataChanged || dataChanged.scope !== 'configurationCustomRules') {
            return;
        }
        if (dataChanged.companyId !== localStorage.getItem('companyId')) {
            return;
        }
        if (dataChanged.payPeriodId && dataChanged.payPeriodId !== payPeriodId) {
            return;
        }
        getCustomRulesData();
    }, [dataChanged]);

    useEffect(() => {
        if (isSearchable) {
            getCustomRulesData();
        }
    }, [debounceSearchValue, isSearchable, selectedCompanyDetails, status]);

    const updatePriority = async (data: any) => {
        try {
            await putApi(
                `/custom-configuration-rule/update/priority?companyId=${localStorage.getItem(
                    'companyId'
                )}&payPeriodId=${payPeriodId}`,
                data
            );
        } catch (error) {
            //
            console.log(error);
        }
    };

    const handleOpen = (data?: any, _isCopy?: boolean) => {
        handleModalClose(true);
        setModalData(data);
        setIsCopy(_isCopy);
    };

    // Opens this screen's own Add Rule modal on behalf of the AI chat, which
    // collects the rule's name and description and then hands the rest over
    // rather than rebuilding the criteria and account pickers in a chat column.
    //
    // A prefilled NEW rule goes in as modalData with isCopy: true — the
    // existing copy-a-rule path, which prefills the form but still POSTs.
    // Plain modalData would switch the modal to edit mode and PUT to an id
    // that does not exist yet.
    const openFromChatRequest = () => {
        // Peek, don't consume: this effect also runs on mount with an empty
        // tableData, and an edit request must survive that pass to be matched
        // once the list arrives.
        const request = peekChatMappingRule();
        if (!request) {
            return;
        }
        // Wait for both APIs before opening anything.
        //
        // The configuration matters most: the modal seeds its Setup Custom
        // Mapping editor with JSON.parse(JSON.stringify(configurations?.settings)),
        // and with settings still undefined that is JSON.parse(undefined),
        // which throws. Clicking Add Rule by hand never hits this because the
        // page has long since loaded; opening it the moment this table mounts
        // does.
        //
        // isLoading covers the rules fetch, which an edit request needs in
        // order to find its rule.
        if (!configurations?.settings || isLoading) {
            return;
        }
        if (request.ruleId) {
            const existing = tableData?.find((rule: any) => rule?.id === request.ruleId);
            if (!existing) {
                // Either the list has not loaded yet, or the rule is on a
                // different pay period. Leave the request pending rather than
                // opening the wrong rule; the tableData dep re-runs this.
                return;
            }
            consumeChatMappingRule();
            handleOpen(existing, false);
            return;
        }
        consumeChatMappingRule();
        handleOpen(
            {
                name: request.name || '',
                description: request.description || '',
                isActive: true,
                // Without this the modal's normalizeCriteria turns absent
                // customer/class into '' — the "Blank Customer" sentinel — so a
                // brand-new rule would open pre-narrowed to blanks instead of Any.
                criteria: {
                    employeeId: 'ANY',
                    customerId: 'ANY',
                    classId: 'ANY',
                    operator1: 'AND',
                    operator2: 'AND',
                },
            },
            true
        );
    };

    useEffect(() => {
        // Mount-time read as well as the event: navigation can mount this
        // component after requestChatMappingRule has already dispatched.
        openFromChatRequest();
        window.addEventListener(CHAT_MAPPING_RULE_EVENT, openFromChatRequest);
        return () => window.removeEventListener(CHAT_MAPPING_RULE_EVENT, openFromChatRequest);
        // Re-runs as each dependency arrives, so a request that could not be
        // acted on yet is picked up as soon as it can be.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData, isLoading, configurations]);

    const handleCloseModal = () => {
        handleModalClose(false);
        setModalData(null);
    };


    return (
        <div>
            <Table
                onChangePriority={async (data: any) => {
                    await updatePriority(data);
                    await getCustomRulesData()
                }}
                getCustomRulesData={getCustomRulesData}
                openAddRuleModal={(data: any, isCopy: any) => handleOpen(data, isCopy)}
                data={tableData}
                isLoading={isLoading}
                employeeOptions={employeeOptions}
                customerOptions={customerList || []}
                classOptions={classOptions}
            />
            {isModalOpen && (
                <CustomConfigurationAddRuleModal
                    handleCloseModal={handleCloseModal}
                    isModalOpen={isModalOpen}
                    modalData={modalData}
                    getCustomRulesData={getCustomRulesData}
                    isCopy={isCopy}
                    payPeriodId={payPeriodId}
                />
            )}
        </div>
    )
}